/**
 * 🤖 AI BOT ANALİZİ — "AI'lar siteni gerçekten okuyor mu?"
 * Sunucu erişim logunu (access.log) yükle → hangi AI botu, ne sıklıkta, hangi sayfaları
 * okuyor. robots.txt "engelli mi" der; bu ekran "GELİYOR MU" der.
 */
import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { useToast } from "@/hooks/use-toast";
import { GlPageHeader, GlSection, GlEmpty, GlButton } from "@/components/ui/gl-kit";
import { GlTableWrap, GlTable, GlTHead, GlTBody, GlTr, GlTh, GlTd, GlBadge } from "@/components/ui/gl-table";
import { BotMessageSquare, Upload, Loader2, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { Fragment } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, ResponsiveContainer } from "recharts";

type Bot = { bot: string; label: string; hits: number; lastSeen: string | null; days: Record<string, number>; pages: { path: string; hits: number }[] };
type UploadRow = { id: number; uploadedAt: string; periodStart: string | null; periodEnd: string | null; totalLines: number; matchedLines: number; data: { bots: Bot[]; googlebotHits: number } };

const TONES = ["#e83e3c", "#ec6461", "#b12220", "#f18e8b", "#d42b29", "#911d1c"];
const fmt = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

export default function BotLog() {
  const { data: domains } = useListDomains();
  const [domainId] = useActiveDomain(domains);
  const { toast } = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [openBot, setOpenBot] = useState<string | null>(null);

  const { data } = useQuery<{ uploads: UploadRow[] }>({
    queryKey: ["bot-logs", domainId],
    enabled: !!domainId,
    queryFn: async () => (await fetch(`/api/domains/${domainId}/bot-logs`)).json(),
  });
  const latest = data?.uploads[0] ?? null;

  const upload = async (file: File) => {
    if (file.size > 24 * 1024 * 1024) { toast({ title: "Dosya çok büyük", description: "25 MB altını yükle — logu tarih aralığına bölebilirsin.", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const text = await file.text();
      const r = await fetch(`/api/domains/${domainId}/bot-logs`, { method: "POST", headers: { "content-type": "text/plain" }, body: text });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error ?? "Yüklenemedi");
      toast({ title: "Log analiz edildi", description: `${fmt(j.totalLines)} satır okundu — ${j.bots} AI botu, ${fmt(j.matchedLines)} AI isteği bulundu.` });
      qc.invalidateQueries({ queryKey: ["bot-logs", domainId] });
    } catch (e) { toast({ title: String(e instanceof Error ? e.message : e), variant: "destructive" }); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  // Günlük grafik: ilk 4 botun gün kırılımı tek grafikte
  const chart = (() => {
    if (!latest?.data.bots.length) return null;
    const top = latest.data.bots.slice(0, 4);
    const days = [...new Set(top.flatMap((b) => Object.keys(b.days)))].sort();
    if (days.length < 2) return null;
    return { rows: days.map((d) => ({ gun: d, ...Object.fromEntries(top.map((b) => [b.bot, b.days[d] ?? 0])) })), bots: top.map((b) => b.bot) };
  })();

  if (!domainId) return <div className="p-4 md:p-6"><GlEmpty title="Önce bir site seç" icon={<BotMessageSquare className="h-6 w-6" />} /></div>;

  return (
    <div className="mx-auto max-w-[1280px] p-4 md:p-6">
      <GlPageHeader
        title="AI Bot Analizi"
        desc="Sunucu erişim logunu yükle — hangi AI botlarının siteni gerçekten okuduğunu, ne sıklıkta geldiklerini ve en çok hangi sayfaları taradıklarını gör."
        icon={<BotMessageSquare className="h-5 w-5" />}
        actions={
          <>
            <input ref={fileRef} type="file" accept=".log,.txt,text/plain" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
            <GlButton onClick={() => fileRef.current?.click()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              access.log Yükle
            </GlButton>
          </>
        }
      />

      {!latest ? (
        /* ÇEKİDÜZEN: "access.log nedir, nereden bulurum?" — hosting'e göre adım adım rehber.
           Teknik bilmeyen kullanıcı için en büyük duvar buydu; tek paragraf yerine sihirbaz. */
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-25 p-6 dark:border-gray-700 dark:bg-white/[0.02]">
          <div className="mb-1 flex items-center gap-2">
            <BotMessageSquare className="h-5 w-5 text-gray-400" />
            <p className="text-theme-sm font-semibold text-gray-700 dark:text-gray-200">İlk analiz için sitenin "ziyaretçi defterini" yükle</p>
          </div>
          <p className="mb-4 max-w-2xl text-theme-xs text-gray-500 dark:text-gray-400">
            Her sunucu, siteye gelen tüm istekleri bir <b>erişim logu</b> (access.log) dosyasına yazar — AI botlarının
            gelip gelmediği de orada. Dosyayı nereden alacağın hostinge göre değişir; kendi durumuna uyan başlığı aç:
          </p>
          <div className="max-w-2xl space-y-2">
            {([
              ["cPanel kullanıyorum (çoğu Türk hosting: Turhost, Natro, Hostinger…)",
                ["cPanel'e giriş yap → \"Metrikler\" (Metrics) bölümünü bul.", "\"Ham Erişim\" (Raw Access) ikonuna tıkla.", "Alan adının yanındaki bağlantıya tıklayınca .gz uzantılı log iner — çift tıklayıp aç.", "Çıkan dosyayı yukarıdaki \"access.log Yükle\" butonuyla yükle."]],
              ["Plesk kullanıyorum",
                ["Plesk'e giriş yap → siteni seç → \"Loglar\" (Logs) menüsüne gir.", "Sağ üstteki indirme simgesinden \"access_ssl_log\" dosyasını indir.", "İndirdiğin dosyayı buraya yükle."]],
              ["Kendi sunucum var (VDS/VPS)",
                ["Nginx kullanıyorsan log şurada: /var/log/nginx/access.log", "Apache kullanıyorsan: /var/log/apache2/access.log (bazı sistemlerde access_log)", "Dosyayı bilgisayarına indirip buraya yükle — 25 MB'tan büyükse son haftaları al."]],
              ["WordPress / hosting paneline erişimim yok",
                ["Log dosyasına yalnız hosting sağlayıcın ulaşabilir — destek hattına şu cümleyi yaz:", "\"Merhaba, siteme ait son 30 günün ham erişim logunu (combined formatta access.log) rica ediyorum.\"", "Gelen dosyayı buraya yükle; gerisini Geolop halleder."]],
            ] as const).map(([baslik, adimlar]) => (
              <details key={baslik} className="group rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
                <summary className="cursor-pointer list-none text-theme-sm font-medium text-gray-800 marker:hidden dark:text-white/90">
                  <span className="mr-1.5 inline-block transition-transform group-open:rotate-90">▸</span>{baslik}
                </summary>
                <ol className="mt-2 list-decimal space-y-1 pl-6 text-theme-xs leading-relaxed text-gray-600 dark:text-gray-300">
                  {adimlar.map((a) => <li key={a}>{a}</li>)}
                </ol>
              </details>
            ))}
          </div>
          <p className="mt-4 max-w-2xl text-[11px] text-gray-400">
            Merak etme: log dosyasında şifre yoktur — sadece "hangi adres, hangi sayfayı, ne zaman istedi" satırları vardır.
            Standart "combined" formatlı tüm loglar desteklenir; dosya analizden sonra özetlenir, ham hali saklanmaz.
          </p>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Özet kartları */}
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {([
              ["AI Bot İsteği", fmt(latest.matchedLines), latest.periodStart ? `${latest.periodStart.slice(0, 10)} → ${latest.periodEnd?.slice(0, 10) ?? ""}` : ""],
              ["Farklı AI Botu", String(latest.data.bots.length), "tespit edildi"],
              ["Googlebot (kıyas)", fmt(latest.data.googlebotHits), "klasik arama botu"],
              ["Okunan Satır", fmt(latest.totalLines), "log satırı"],
            ] as const).map(([l, v, h]) => (
              <div key={l} className="gl-card">
                <span className="gl-label">{l}</span>
                <h4 className="gl-metric">{v}</h4>
                {h && <p className="mt-1 text-theme-xs text-gray-400">{h}</p>}
              </div>
            ))}
          </div>

          {latest.data.bots.length === 0 && (
            <div className="rounded-xl border border-warning-200 bg-warning-50 p-4 text-theme-sm text-gray-700 dark:border-warning-500/30 dark:bg-warning-500/10 dark:text-gray-200">
              Bu log aralığında hiçbir AI botu siteye <strong>gelmemiş</strong>. robots.txt engeli yoksa bu, sitenin AI dizinlerinde henüz keşfedilmediğini gösterir — GEO çalışmasının tam hedefi bu.
            </div>
          )}

          {/* Bot tablosu */}
          {!!latest.data.bots.length && (
            <GlSection title="Bot Kırılımı" desc="Satıra tıkla — o botun en çok okuduğu sayfalar açılır." bodyClass="!px-0 !py-0">
              <GlTableWrap>
                <GlTable>
                  <GlTHead><GlTr><GlTh>Bot</GlTh><GlTh align="right">İstek</GlTh><GlTh>Son Görülme</GlTh><GlTh>Ne İşe Yarar</GlTh></GlTr></GlTHead>
                  <GlTBody>
                    {latest.data.bots.map((b) => (
                      <Fragment key={b.bot}>
                        <GlTr onClick={() => setOpenBot(openBot === b.bot ? null : b.bot)}>
                          <GlTd>
                            <span className="inline-flex items-center gap-1.5 font-medium text-gray-800 dark:text-white/90">
                              {openBot === b.bot ? <ChevronDown className="h-3.5 w-3.5 text-gray-400" /> : <ChevronRight className="h-3.5 w-3.5 text-gray-400" />}
                              {b.bot}
                            </span>
                          </GlTd>
                          <GlTd align="right"><span style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(b.hits)}</span></GlTd>
                          <GlTd>{b.lastSeen ? <GlBadge color="success">{b.lastSeen}</GlBadge> : "—"}</GlTd>
                          <GlTd><span className="text-theme-xs text-gray-500">{b.label.replace(`${b.bot} (`, "").replace(/\)$/, "")}</span></GlTd>
                        </GlTr>
                        {openBot === b.bot && (
                          <GlTr>
                            <GlTd colSpan={4} className="!py-4 bg-gray-25 dark:bg-white/[0.02]">
                              <p className="mb-2 text-theme-xs font-semibold uppercase tracking-wide text-gray-500">En çok okuduğu sayfalar</p>
                              <div className="grid gap-1.5 md:grid-cols-2">
                                {b.pages.map((p) => (
                                  <div key={p.path} className="flex items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-theme-xs dark:border-gray-800">
                                    <span className="truncate text-gray-700 dark:text-gray-300">{p.path}</span>
                                    <span className="shrink-0 font-semibold" style={{ fontVariantNumeric: "tabular-nums" }}>{p.hits}</span>
                                  </div>
                                ))}
                              </div>
                            </GlTd>
                          </GlTr>
                        )}
                      </Fragment>
                    ))}
                  </GlTBody>
                </GlTable>
              </GlTableWrap>
            </GlSection>
          )}

          {/* Zaman grafiği */}
          {chart && (
            <GlSection title="Günlük AI Bot Trafiği" desc="İlk 4 botun gün gün istek sayısı.">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={chart.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e7e4" />
                  <XAxis dataKey="gun" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <RTooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {chart.bots.map((b, i) => <Line key={b} type="monotone" dataKey={b} stroke={TONES[i % TONES.length]} strokeWidth={2} dot={false} />)}
                </LineChart>
              </ResponsiveContainer>
            </GlSection>
          )}

          {/* Geçmiş yüklemeler */}
          {data && data.uploads.length > 1 && (
            <GlSection title="Önceki Analizler" bodyClass="!px-0 !py-0">
              <GlTableWrap>
                <GlTable>
                  <GlTHead><GlTr><GlTh>Yükleme</GlTh><GlTh>Dönem</GlTh><GlTh align="right">AI İsteği</GlTh><GlTh /></GlTr></GlTHead>
                  <GlTBody>
                    {data.uploads.slice(1).map((u) => (
                      <GlTr key={u.id}>
                        <GlTd>{new Date(u.uploadedAt).toLocaleString("tr-TR")}</GlTd>
                        <GlTd><span className="text-theme-xs text-gray-500">{u.periodStart?.slice(0, 10)} → {u.periodEnd?.slice(0, 10)}</span></GlTd>
                        <GlTd align="right">{fmt(u.matchedLines)}</GlTd>
                        <GlTd align="right">
                          <button className="text-gray-400 hover:text-error-500" onClick={async () => { await fetch(`/api/domains/${domainId}/bot-logs/${u.id}`, { method: "DELETE" }); qc.invalidateQueries({ queryKey: ["bot-logs", domainId] }); }}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </GlTd>
                      </GlTr>
                    ))}
                  </GlTBody>
                </GlTable>
              </GlTableWrap>
            </GlSection>
          )}
        </div>
      )}
    </div>
  );
}
